/* eslint-disable react-native/no-inline-styles */
/* eslint-disable react/prop-types */
/*
Author: Gilang Romadhanu Tartila
*/

import React, { useState } from 'react';
import { View, StyleSheet, Dimensions, TouchableOpacity } from 'react-native';
import Toast from 'react-native-toast-message';
import { getDocumentAsync } from 'expo-document-picker';
import { useDispatch } from 'react-redux';

import SociollaText from '@components/sociolla/SociollaText';
import ItemDetail from '@components/global/ItemDetail';
import TitleSubSection from '@components/global/TitleSubSection';
import Image from '@components/global/Image';
import Button from '@components/form/Button';

import ESignature from '@/screens/inventory/components/ESignature';
import { showPopup } from '@/store/modal';
import Colors from '@global/colors';
import { updateOrderSignaturesById } from '@api/ms-order';
import { isWeb, isNative, isMobileWeb } from '@global/screens';
import {
  convertFullAddress,
  parseDate,
  currency,
  convertUnderscoreString,
  isVn,
  hasNoAccess,
  uploadS3,
  downloadFile,
  firstLetter,
  isAllowedAttachmentFormat,
} from '@global/helpers';
import { MsOrder } from '@api/ms-order';
import { showAlert } from '@/store/global';
import {
  ATTACHMENT_FORMAT_ERROR_TITLE,
  ATTACHMENT_FORMAT_ERROR_DESCRIPTION,
} from '@/constants';

const dimension = Dimensions.get('window');

const OrderHeader = (props) => {
  const { testID = 'sales-order-header' } = props;
  const dispatch = useDispatch();

  const allowedRoles = [
    'superadmin',
    'general',
    'sales_admin',
    'sales_supervisor',
    'salesman',
    'chief_of_staff',
  ];
  const hasAccessEditSignatures =
    !hasNoAccess(allowedRoles) &&
    props.detailData?.status !== 'need_approval' &&
    props.detailData?.status !== 'approved';

  const hasAccessEditSignaturesCustomers = !hasNoAccess(allowedRoles);

  const [signatures, setSignatures] = useState({
    salesman: props.detailData?.signatures?.salesman || '',
    customer: props.detailData?.signatures?.customer || '',
  });
  const detailData =
    props.detailData && Object.keys(props.detailData).length
      ? props.detailData
      : {};
  const detailCustomer =
    props.detailCustomer && Object.keys(props.detailCustomer).length
      ? props.detailCustomer
      : {};
  const totalData =
    props.totalData && Object.keys(props.totalData).length
      ? props.totalData
      : { invoices: 0, deliveries: 0 };
  const isConfirm = detailData.status == 'need_approval';

  const detailLeft = [
    {
      name: 'Created By',
      value: detailData.is_quotation
        ? 'API Integration'
        : detailData.created_by && detailData.created_by.name
        ? detailData.created_by.name
        : '-',
    },
    {
      name: 'Customer',
      isOnPressed: true,
      valueStyle: { color: Colors.blue0 },
      value:
        detailData.customer && detailData.customer.name
          ? detailData.customer.name
          : '-',
      onPress: () => {
        props.navigation.navigate('CustomerNavigator', {
          screen: 'CustomerDetailScreen',
          params: {
            id: detailData.customer?.id,
            order_id: detailData._id,
            name: detailData.customer?.name,
            prev_page: 'sales-order',
            reference_code: detailData.reference_code,
            order_type: detailData.type,
          },
        });
      },
    },
    {
      name: 'Invoice Address',
      value: detailData.invoice_address
        ? convertFullAddress(detailData.invoice_address, true)
        : '-',
    },
    {
      name: 'Delivery Address',
      value: detailData.delivery_address
        ? convertFullAddress(detailData.delivery_address, true)
        : '-',
    },
    {
      name: 'Address',
      value: detailData.delivery_address
        ? convertFullAddress(detailData.delivery_address)
        : '-',
    },
    {
      name: 'Open Receivable',
      value: detailData.open_receivable
        ? currency(detailData.open_receivable, true)
        : '-',
    },
    {
      name: 'Overdue Receivable',
      value: detailData.overdue_recievable
        ? currency(detailData.overdue_recievable, true)
        : '-',
    },
    {
      name: 'Order date',
      value: detailData.order_date
        ? parseDate(detailData.order_date, 'dd MMM yyyy HH:mm:ss')
        : '-',
    },
  ];

  const detailRight = [
    { name: 'Pricelist', value: detailData?.price_list?.name || '-' }, // 'Pricelist Others (IDR)'
    {
      name: 'Payment terms',
      value: detailData?.cash_before_delivery?.percentage
        ? 'Cash Before Delivery (CBD)'
        : detailCustomer?.payment_terms
        ? convertUnderscoreString(
            detailCustomer.payment_terms?.name
              ? detailCustomer.payment_terms.name
              : detailCustomer.payment_terms,
          )
        : '-',
    },
    {
      name: 'Sales Person',
      value:
        detailData.sales_person && detailData.sales_person.name
          ? detailData.sales_person.name
          : detailData?.user?.name
          ? detailData.user.name
          : '-',
    },
    {
      name: 'Sales Person Code',
      value: detailData?.user?.sales_code || '-',
    },
    {
      name: 'Sales Team',
      value: detailData?.sales_team?.name
        ? detailData.sales_team.name
        : detailData?.user?.team_name
        ? detailData.user.team_name
        : '-',
    },
    { name: 'Business Unit', value: 'B2B' },
    {
      name: 'Distribution Fee',
      value: detailData.distribution_fee_amount
        ? currency(detailData.distribution_fee_amount, true)
        : '-',
    },
    {
      name: 'Delivery Status',
      value: detailData.delivery_status ? detailData.delivery_status : '-',
    },
  ];

  const detailSales = [
    {
      name: 'Customer PO Number',
      value: detailData?.customer_po_number || '-',
    },
    {
      name: 'Customer PO Date',
      value: detailData.customer_po_date
        ? parseDate(detailData.customer_po_date)
        : '-',
    },
    {
      name: 'Expiration Date',
      value: detailData?.expiration_date
        ? parseDate(detailData.expiration_date)
        : '-',
    },
    {
      name: 'Market Type',
      value: detailCustomer?.market_type?.code
        ? detailCustomer.market_type.code.toUpperCase()
        : '-',
    },
    {
      name: 'Business Type',
      value: detailCustomer?.market_type?.business_type?.code
        ? detailCustomer.market_type.business_type.code
        : '-',
    },
    {
      name: 'Type',
      value: detailData?.type ? firstLetter(detailData.type) : '-',
    },
    {
      name: 'Cash Before Delivery',
      value: detailData?.cash_before_delivery?.percentage
        ? `${detailData.cash_before_delivery.percentage}%`
        : '-',
    },
    {
      name: 'Reference Code Cerebro',
      value: detailData?.cerebro_reference_code
        ? `${detailData.cerebro_reference_code}`
        : '-',
    },
    ...(detailData?.status === 'cancelled'
      ? [
          {
            name: 'Cancelled By',
            value: detailData?.cancelled_by?.name
              ? `${detailData?.cancelled_by?.name}`
              : '-',
          },
          {
            name: 'Cancelled Reason',
            value: detailData?.cancelled_reason
              ? `${detailData?.cancelled_reason}`
              : '-',
          },
        ]
      : []),
    ...(detailData?.cash_before_delivery?.id
      ? [
          {
            name: 'Payment Proof',
            valueComponent: detailData?.payment_proof ? (
              <View style={styles.documentItemContainer}>
                <View style={styles.documentItemList}>
                  <SociollaText
                    numberOfLines={isWeb ? 2 : 1}
                    ellipsizeMode="tail"
                    style={{
                      fontSize: 12,
                      color: Colors.grey1,
                      textAlign: 'left',
                    }}>
                    Payment Proof
                  </SociollaText>
                </View>

                <TouchableOpacity
                  style={{ marginLeft: 10 }}
                  onPress={() =>
                    downloadFile(
                      detailData?.payment_proof,
                      'payment-proof',
                      '',
                      true,
                    )
                  }>
                  <Image
                    style={{ width: 24, height: 24 }}
                    width={24}
                    height={24}
                    isResolveAsset
                    source={require('@/assets/img/icons/attachment.png')}
                  />
                </TouchableOpacity>

                <TouchableOpacity
                  style={{ marginLeft: 12 }}
                  onPress={() => removePaymentProof()}>
                  <Image
                    style={{ width: 20, height: 20 }}
                    width={20}
                    height={20}
                    svg
                    source={require('@assets/img/icons/close-circle-grey.svg')}
                  />
                </TouchableOpacity>
              </View>
            ) : (
              <View>
                <Button
                  testID="sales-order-header-upload-payment-proof-btn"
                  type="grey"
                  rounded={true}
                  buttonStyle={{ width: 70 }}
                  title={
                    <SociollaText style={{ fontSize: 10, color: Colors.black }}>
                      Upload
                    </SociollaText>
                  }
                  onPress={async () => await uploadPaymentProof()}
                />
              </View>
            ),
          },
        ]
      : []),
  ];

  const detailDelivery = [
    {
      name: 'Warehouse',
      value: detailData?.user?.team_warehouse?.name
        ? detailData.user.team_warehouse.name
        : '-',
    },
    {
      name: 'Delivery Date',
      value: `Expected ${
        detailData.delivered_at ? parseDate(detailData.delivered_at) : '-'
      }`,
    },
  ];

  const goToScreen = (screen) => {
    let titleScreen = screen;
    if (screen == 'SalesOrderInvoiceScreen') {
      titleScreen = `Invoices (${totalData.invoices})`;
    } else if (screen == 'SalesOrderDeliveryScreen') {
      titleScreen = `Deliveries (${totalData.deliveries})`;
    } else if (screen == 'SalesOrderDocumentScreen') {
      titleScreen = `Attactments (${detailData?.attachments?.length || 0})`;
    } else if (screen == 'SalesOrderPaymentProofScreen') {
      titleScreen = 'Payment Proof';
    }
    props.navigation.navigate(screen, {
      id: props.route.params.id,
      name: titleScreen,
      titleScreen: titleScreen,
      reference: detailData.reference_code,
      salesOrderId: detailData._id,
      salesOrderName: detailData?.customer?.name
        ? detailData.customer.name
        : '-',
    });
  };

  const updateSignaturesOrder = async (payload) => {
    try {
      const fixedPayload = {
        ...payload,
      };
      dispatch(showPopup({ name: 'isLoading', value: true }));
      await updateOrderSignaturesById(detailData._id, fixedPayload);
      dispatch(showPopup({ name: 'isLoading', value: false }));
      Toast.show({ text1: 'Successfully update signatures', type: 'success' });
    } catch (error) {
      console.log(error);
      dispatch(showPopup({ name: 'isLoading', value: false }));
      Toast.show({
        text1: 'Something Wrong, please try again.',
        type: 'error',
      });
    }
  };

  const removePaymentProof = () => {
    dispatch(
      showAlert({
        show: true,
        title: 'Remove Payment Proof',
        description: 'Are you sure you want to remove this attachment?',
        isConfirm: true,
        testID: 'sales-order-header-remove-payment',
        onCallback: async () => {
          dispatch(
            showPopup({
              name: 'isLoading',
              value: true,
            }),
          );
          await MsOrder.deletePaymentProofOrder(detailData?._id);
          dispatch(
            showPopup({
              name: 'isLoading',
              value: false,
            }),
          );
          if (props.getDetail) {
            props.getDetail(detailData?._id);
          }
        },
      }),
    );
  };

  const uploadPaymentProof = async () => {
    const attach = await getDocumentAsync({
      copyToCacheDirectory: false,
    });
    if (!attach.canceled) {
      if (!isAllowedAttachmentFormat(attach.assets[0])) {
        dispatch(
          showAlert({
            show: true,
            title: ATTACHMENT_FORMAT_ERROR_TITLE,
            description: ATTACHMENT_FORMAT_ERROR_DESCRIPTION,
            isConfirm: false,
          }),
        );
        return;
      }
      dispatch(
        showPopup({
          name: 'isLoading',
          value: true,
        }),
      );
      try {
        const url = await uploadS3({
          file: attach?.assets[0] || {},
          isNeedMobileSupport: true,
        });
        if (url) {
          await MsOrder.updatePaymentProofOrder(detailData?._id, {
            payment_proof: url,
          });
          if (props.getDetail) {
            props.getDetail(detailData?._id);
          }
        } else {
          Toast.show({
            text1: 'Failed to upload file. Please try again.',
            type: 'error',
          });
        }
      } catch (err) {
        Toast.show({ text1: 'A system error occurred', type: 'error' });
      } finally {
        dispatch(
          showPopup({
            name: 'isLoading',
            value: false,
          }),
        );
      }
    }
  };

  const renderSignatures = () => {
    return (
      <View
        style={{
          display: 'flex',
          flexDirection: isWeb ? 'row' : 'column',
          gap: 10,
          marginTop: 20,
          marginHorizontal: 10,
        }}>
        <ESignature
          hasEdit={hasAccessEditSignatures}
          onlyDraw={true}
          name={signatures.salesman?.name}
          showName={true}
          label="Salesman"
          esignImage={signatures.salesman?.url}
          dateSigned={signatures.salesman?.created_at}
          onChange={async (v) => {
            const updated = {
              ...signatures,
              salesman: {
                ...signatures.salesman,
                url: v,
                name: signatures.salesman?.name,
              },
            };
            setSignatures(updated);
            await updateSignaturesOrder({
              type: 'salesman',
              url: v,
              name: signatures.salesman?.name,
            });

            if (typeof props.onUpdateSignatures === 'function') {
              props.onUpdateSignatures(updated);
            }
          }}
          onChangeName={(v) => {
            setSignatures({
              ...signatures,
              salesman: {
                ...signatures.salesman,
                name: v,
              },
            });
          }}
        />
        <ESignature
          hasEdit={hasAccessEditSignaturesCustomers}
          onlyDraw={true}
          name={signatures.customer?.name}
          showName={true}
          label="Customer"
          esignImage={signatures.customer?.url}
          dateSigned={signatures.customer?.created_at}
          onChange={async (v) => {
            const updated = {
              ...signatures,
              customer: {
                ...signatures.customer,
                url: v,
                name: signatures.customer?.name,
              },
            };
            setSignatures(updated);
            await updateSignaturesOrder({
              type: 'customer',
              url: v,
              name: signatures.customer?.name,
            });

            if (typeof props.onUpdateSignatures === 'function') {
              props.onUpdateSignatures(updated);
            }
          }}
          onChangeName={(v) => {
            setSignatures({
              ...signatures,
              customer: {
                ...signatures.customer,
                name: v,
              },
            });
          }}
        />
      </View>
    );
  };

  return (
    <View
      testID={testID}
      style={[
        styles.wrapper,
        isConfirm ? { paddingBottom: !isNative ? 15 : 70 } : {},
      ]}>
      <View
        style={{
          flexDirection: 'row',
          justifyContent: isWeb ? 'flex-start' : 'space-between',
          padding: 15,
          ...(!isWeb ? { flexWrap: 'wrap' } : {}),
        }}>
        <TouchableOpacity
          testID="sales-order-header-invoices-tab"
          activeOpacity={0.6}
          style={[styles.tabItem, isWeb ? { marginRight: 15 } : {}]}
          onPress={() => goToScreen('SalesOrderInvoiceScreen')}>
          <SociollaText type="medium" style={styles.tabText}>
            Invoices ({totalData.invoices})
          </SociollaText>
        </TouchableOpacity>
        <TouchableOpacity
          testID="sales-order-header-deliveries-tab"
          activeOpacity={0.6}
          style={[styles.tabItem, isWeb ? { marginRight: 15 } : {}]}
          onPress={() => goToScreen('SalesOrderDeliveryScreen')}>
          <SociollaText type="medium" style={styles.tabText}>
            Deliveries ({totalData.deliveries})
          </SociollaText>
        </TouchableOpacity>
        <TouchableOpacity
          testID="sales-order-header-attachments-tab"
          activeOpacity={0.6}
          style={[
            styles.tabItem,
            isWeb ? { marginRight: 15 } : { marginTop: 15 },
          ]}
          onPress={() => goToScreen('SalesOrderDocumentScreen')}>
          <SociollaText type="medium" style={styles.tabText}>
            Attachments ({detailData?.attachments?.length || 0})
          </SociollaText>
        </TouchableOpacity>
      </View>
      <View
        style={{
          width: !isNative ? '100%' : dimension.width,
          marginTop: 15,
          flexDirection: isWeb ? 'row' : 'column',
          padding: 15,
        }}>
        <View
          style={{
            width: isWeb ? '50%' : '100%',
            paddingRight: isWeb ? 15 : 0,
          }}>
          {detailLeft.length > 0 &&
            detailLeft.map((item, idx) => (
              <ItemDetail
                name={item.name}
                value={item.value}
                key={idx}
                withoutBorder={isWeb ? idx == detailLeft.length - 1 : false}
                onPress={item.isOnPressed ? item.onPress : () => {}}
                valueStyle={item.valueStyle ? item.valueStyle : {}}
              />
            ))}
        </View>
        <View
          style={{
            width: isWeb ? '50%' : '100%',
            paddingLeft: isWeb ? 15 : 0,
          }}>
          {detailRight.length > 0 &&
            detailRight.map((item, idx) => (
              <ItemDetail
                name={item.name}
                value={item.value}
                key={idx}
                withoutBorder={idx == detailRight.length - 1}
              />
            ))}
        </View>
      </View>
      <View>
        <TitleSubSection title="SALES" />
        <View style={styles.sectionContent}>
          {detailSales.length > 0 &&
            detailSales.map((item, idx) => (
              <ItemDetail
                name={item.name}
                value={item.value ? item.value : null}
                valueComponent={
                  item.valueComponent ? item.valueComponent : null
                }
                key={idx}
                withoutBorder={idx == detailSales.length - 1}
              />
            ))}
        </View>
        <TitleSubSection title="DELIVERY" />
        <View
          style={[
            styles.sectionContent,
            isConfirm
              ? { paddingTop: 15, paddingBottom: !isNative ? 15 : 70 }
              : {},
          ]}>
          {detailDelivery.length > 0 &&
            detailDelivery.map((item, idx) => (
              <ItemDetail
                name={item.name}
                value={item.value}
                key={idx}
                withoutBorder={idx == detailDelivery.length - 1}
              />
            ))}
        </View>
        {!isVn() && (
          <>
            <TitleSubSection title="E - SIGNATURE" />
            {renderSignatures()}
          </>
        )}
        {isMobileWeb && <View style={{ height: 200 }} />}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  wrapper: {
    width: '100%',
  },
  // eslint-disable-next-line react-native/no-color-literals
  tabItem: {
    width: isWeb ? '23%' : '47%',
    maxWidth: isWeb ? 200 : '47%',
    borderRadius: 4,
    backgroundColor: '#00B5DF25',
    paddingVertical: 15,
    paddingHorizontal: 10,
  },
  tabText: {
    fontSize: 14,
    color: Colors.grey2,
  },
  sectionContent: {
    width: '100%',
    paddingHorizontal: 15,
  },
  documentItemContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
    flexWrap: 'wrap',
  },
  documentItemList: {
    width: isWeb ? 180 : 140,
    borderWidth: 1,
    borderColor: Colors.grey0,
    borderRadius: 6,
    paddingVertical: 6,
    paddingHorizontal: 12,
    backgroundColor: Colors.white,
    alignItems: 'center',
    justifyContent: 'center',
  },
});

export default OrderHeader;
